import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useIsFetching, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "../../components/ui/button";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { QueueTable, Td } from "../../features/web3/forex/components/table";
import { formatDateTime } from "../../lib/format";
import { adminPost, useCan, useForexAdmin } from "./api";
import {
  AccountLink,
  BASE,
  DealCard,
  Drawer,
  Money,
  Panel,
  PeriodLink,
  QueryState,
  Stat,
  Unavail,
  UserLink,
  inputCls,
  labelCls,
  linkCls,
  mono,
  muted,
  orDash,
  useOpen,
} from "./parts";
import { CONNECTION_CHARGE_STATE, ReleaseRetry, COPY_DIRECTION, END_CAUSE, connectionStatus, equityFloorText, limitText, marginCapText, paidToLabel, usdText } from "./rows";
import { ChargeDrawer } from "./Settlements";
import { RecordHistory } from "./Audit";

const UNPAID_TONE = {
  RUNNING: "text-emerald-400",
  SETTLING: "text-muted-foreground",
  DUE: "text-amber-400",
  CARRIED: "text-orange-400",
  UNRECOVERED_LOSS: "text-sky-400",
  BEING_SETTLED: "text-red-400",
};
// 015's labels.
const UNPAID_LABEL = {
  RUNNING: "Running",
  SETTLING: "Settling",
  DUE: "Due",
  CARRIED: "Carried",
  UNRECOVERED_LOSS: "Unrecovered loss",
  BEING_SETTLED: "Being checked",
};

const windowOf = (r) => `${formatDateTime(r.startsAt)} → ${formatDateTime(r.endsAt)}`;

const UNPAID_COLUMNS = [{ label: "Status" }, { label: "Window" }, { label: "Cut", align: "right" }, { label: "Profit", align: "right" }];
// RUNNING and SETTLING are not recorded yet: their live profit is in the running share.
const unpaidRow = (r) => (
  <tr key={r.startsAt}>
    <Td className={`text-xs font-medium ${UNPAID_TONE[r.status]}`}>{UNPAID_LABEL[r.status] ?? r.status}</Td>
    <Td className={muted}>{windowOf(r)}</Td>
    <Td align="right" className={mono}>{r.cutPercent}%</Td>
    <Td align="right">{r.profit == null ? <span className="text-xs text-muted-foreground">not recorded yet</span> : <Money value={r.profit} />}</Td>
  </tr>
);

const SETTLEMENT_COLUMNS = [
  { label: "Window" }, { label: "Profit", align: "right" }, { label: "Cut", align: "right" },
  { label: "Carried" }, { label: "Paid" }, { label: "Charge" }, { label: "Period" }, { label: "Created" },
];

const CHARGE_COLUMNS = [
  { label: "Ref" }, { label: "State" }, { label: "Created" }, { label: "Profit", align: "right" },
  { label: "Cut", align: "right" }, { label: "Share", align: "right" }, { label: "Paid to" }, { label: "Debit" }, { label: "Credit" },
  { label: "Refund" }, { label: "Release at" }, { label: "Released" }, { label: "Period" }, { label: "Deals" },
];

// The copy engine's copied trades on this connection, counted by state (Ginox's
// records, so never null). FROZEN and UNKNOWN are the ones that need hands on.
const COPY_STATES = [
  ["OPENING", "Opening"], ["OPEN", "Open"], ["CLOSING", "Closing"], ["CLOSED", "Closed"],
  ["CLOSED_OUTSIDE", "Closed outside"], ["SKIPPED", "Skipped"], ["UNKNOWN", "Unknown"], ["FROZEN", "Frozen"],
];
const NEEDS_HANDS = new Set(["UNKNOWN", "FROZEN"]);

// A charge's deals come unlabelled (matched by ticket, never by comment), so name
// each by the ticket it sits on; the sign is only the fallback.
const dealLabel = (ch, d) => {
  const t = String(d.deal);
  if (t === ch.refundTicket) return "Refund";
  if (t === ch.debitTicket) return "Debit";
  if (t === ch.creditTicket) return "Credit";
  return d.amount.startsWith("-") ? "Debit" : "Credit";
};

const errBox = "rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300";

const END_CAUSES = [
  { value: "COPIER_GONE", label: "The copier's account is gone from MT5" },
  { value: "MASTER_GONE", label: "The master's account is gone from MT5" },
];

// 023: end a connection whose copier's or master's account is gone from MT5. The
// per-connection pause is gone since v1.1.0 (its routes 404); only the copy
// engine's global pause remains. The write answers the new state, so the page refetches.
function ConnectionActions({ c }) {
  const can = useCan();
  const qc = useQueryClient();
  // The paused banner reads the copy engine, so only with copy_engine.read (025).
  const engine = useForexAdmin(can("copy_engine.read") ? "/copy-engine/admin/status" : null);
  const [ending, setEnding] = useState(false);
  const [cause, setCause] = useState("");
  const done = () => qc.invalidateQueries({ queryKey: ["forex-admin"] });

  const end = useMutation({
    mutationFn: () => adminPost(`/connections/admin/${c.id}/end`, { cause }),
    onSuccess: () => {
      done();
      setEnding(false);
    },
    // "already ended" means it went through: show the page as it is now.
    onError: (err) => {
      if (err.status === 409 && /already ended/i.test(err.message)) {
        done();
        setEnding(false);
      }
    },
  });
  const close = () => {
    setEnding(false);
    setCause("");
    end.reset();
  };

  return (
    <div className="space-y-3">
      {engine.data?.paused && (
        <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
          All copying is paused on the copy engine, so no connection opens new copies until it is resumed.
        </p>
      )}
      {can("connections.end") && (
        <div className="flex flex-wrap items-center gap-3">
          <Button size="sm" variant="destructive" onClick={() => setEnding(true)}>End connection</Button>
        </div>
      )}

      <Drawer open={ending} onClose={close} title="End connection">
        <p className="text-sm text-muted-foreground">
          Only for a connection whose copier's or master's account is gone from the trading server. A normal
          disconnect is the copier's to make. The account named below is checked on MT5 first; if it is still
          there, this is refused.
        </p>
        <div>
          <label className={labelCls}>Which account is gone</label>
          <select value={cause} onChange={(e) => setCause(e.target.value)} className={inputCls}>
            <option value="">Choose…</option>
            {END_CAUSES.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        {/* 023: ending never closes the copier's copied trades. */}
        {cause === "MASTER_GONE" && (
          <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
            The copier's account still exists, and any copied trade open on it stays open and is no longer closed
            by the engine. Check it on MT5 and close them by hand. A leftover position also blocks the copier's
            next connect.
          </p>
        )}
        {end.error && <p className={errBox}>{end.error.message}</p>}
        <Button size="sm" variant="destructive" disabled={!cause || end.isPending} onClick={() => end.mutate()}>
          {end.isPending ? "Ending…" : "End connection"}
        </Button>
      </Drawer>
    </div>
  );
}

// share figures may carry a null cut when nothing is in that bucket.
const Share = ({ label, s }) => (
  <Stat label={label}>
    {s ? <Money value={s.share} /> : "—"}
    {/* cutPercent is null when nothing is in the bucket; its profit still shows. */}
    {s && (
      <span className="ml-2 text-xs text-muted-foreground">
        of {s.profit}{s.cutPercent != null && ` at ${s.cutPercent}%`}
      </span>
    )}
  </Stat>
);

export default function ConnectionDetail() {
  const { id } = useParams();
  const can = useCan();
  const canCharge = can("settlements.read");
  const q = useForexAdmin(`/connections/admin/${id}`);
  const [charge, setCharge] = useOpen("charge");
  const qc = useQueryClient();
  const fetching = useIsFetching({ queryKey: ["forex-admin"] }) > 0;

  return (
    <QueueLayout
      eyebrow="Forex Admin · Connection"
      title="Connection"
      isFetching={fetching}
      onRefresh={() => qc.invalidateQueries({ queryKey: ["forex-admin"] })}
    >
      <Link to={`${BASE}/connections`} className={`text-xs text-muted-foreground ${linkCls}`}>← All connections</Link>
      <QueryState q={q}>
        {(c) => {
          const [status, tone] = connectionStatus(c);
          const rs = c.runningShare;
          const copiedTo = c.endedAt ?? new Date().toISOString();
          return (
            <div className="space-y-10">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Stat label="Status"><span className={tone}>{status}</span></Stat>
                <Stat label="Copier"><UserLink username={c.username} /></Stat>
                <Stat label="Copier login">{c.accountId ? <AccountLink id={c.accountId}>{c.login}</AccountLink> : "Removed"}</Stat>
                <Stat label="Master">
                  <Link to={`${BASE}/masters/${c.master.id}`} className={linkCls}>{c.master.name ?? "Unnamed"}</Link>
                </Stat>
                <Stat label="Copier tier">{orDash(c.tier)}</Stat>
                <Stat label="Master login">{orDash(c.master.login)}</Stat>
                <Stat label="Equity ratio">{c.equityRatio}%</Stat>
                <Stat label="Direction"><span className="text-sm">{COPY_DIRECTION[c.copyDirection] ?? "—"}</span></Stat>
                <Stat label="Loss limit">{limitText(c.lossLimit)}</Stat>
                <Stat label="Profit target">{limitText(c.profitTarget)}</Stat>
                <Stat label="Margin cap">{marginCapText(c.marginCap)}</Stat>
                <Stat label="Position cap">{c.positionCap ?? "None"}</Stat>
                <Stat label="Equity stop">{marginCapText(c.equityStop)}</Stat>
                <Stat label="Stop amount">{usdText(c.equityStopAmount)}</Stat>
                <Stat label="Equity floor">{equityFloorText(c)}</Stat>
                <Stat label="Started"><span className="text-xs">{formatDateTime(c.createdAt)}</span></Stat>
                {c.endedAt && (
                  <>
                    <Stat label="Ended"><span className="text-xs">{formatDateTime(c.endedAt)}</span></Stat>
                    {/* Null on a connection ended before causes were kept. */}
                    <Stat label="End cause"><span className="text-sm">{END_CAUSE[c.endCause] ?? "—"}</span></Stat>
                  </>
                )}
              </div>

              {c.status !== "ENDED" && <ConnectionActions c={c} />}

              <Panel title="What the copier owes">
                {rs ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                      {/* running is null once the connection has ended (010). */}
                      <Stat label="Running period profit">
                        {rs.running ? <Money value={rs.running.profit} /> : "—"}
                        {rs.running && (
                          <span className="block text-xs text-muted-foreground">
                            {windowOf(rs.running)} at {rs.running.cutPercent}%
                          </span>
                        )}
                      </Stat>
                      <Share label="Due" s={rs.due} />
                      <Share label="Carried" s={rs.carried} />
                      {/* v1.20.0 (034): never owed, kept apart from Due and Carried. */}
                      <Stat label="Loss to recover"><Money value={rs.unrecoveredLoss} /></Stat>
                      <Share label="Being checked" s={rs.beingSettled} />
                      <Share label="Running share (estimate)" s={rs.runningShare} />
                    </div>
                    {/* Ended periods not recorded yet; about a minute, usually none. */}
                    <p className="text-xs text-muted-foreground">
                      Settling:{" "}
                      {rs.settling.length === 0
                        ? "none"
                        : rs.settling.map((w) => `${windowOf(w)} at ${w.cutPercent}%, profit ${w.profit}`).join(" · ")}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    <Unavail /> Live share unavailable: the trading figures could not be read, or the copier removed the account.
                  </p>
                )}
              </Panel>

              <Panel title="Unpaid windows">
                <QueueTable columns={UNPAID_COLUMNS} rows={c.unpaid} renderRow={unpaidRow} />
              </Panel>

              <Panel
                title="Copied trades"
                action={
                  c.accountId && (
                    // Copies are dealer deals (comment ginox-copy-<id>), not bridge ones, so
                    // this lists every trade on the account in the connection's window.
                    <Link
                      className={`text-xs ${linkCls}`}
                      to={`${BASE}/accounts/${c.accountId}?${new URLSearchParams({
                        tab: "deals", action: "TRADE", from: c.createdAt, to: copiedTo,
                      })}`}
                    >
                      Account trades in this window →
                    </Link>
                  )
                }
              >
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {COPY_STATES.map(([key, label]) => (
                    <Stat
                      key={key}
                      label={label}
                      tone={NEEDS_HANDS.has(key) && c.copiedTrades[key] > 0 ? "text-amber-400" : undefined}
                    >
                      {c.copiedTrades[key]}
                    </Stat>
                  ))}
                </div>
              </Panel>

              <Panel
                title="Latest 20 settlements"
                action={<Link to={`${BASE}/settlements?tab=settlements&connection=${c.id}`} className={`text-xs ${linkCls}`}>All →</Link>}
              >
                <QueueTable
                  columns={SETTLEMENT_COLUMNS}
                  rows={c.settlements}
                  renderRow={(s) => (
                    <tr key={s.id}>
                      <Td className={muted}>{windowOf(s)}</Td>
                      <Td align="right"><Money value={s.profit} /></Td>
                      <Td align="right" className={mono}>{s.cutPercent}%</Td>
                      <Td className={muted}>{s.carriedAt ? formatDateTime(s.carriedAt) : "—"}</Td>
                      <Td className={muted}>{s.paidAt ? formatDateTime(s.paidAt) : "—"}</Td>
                      <Td className="text-xs">
                        {s.chargeId ? (
                          canCharge ? (
                            <button type="button" onClick={() => setCharge(s.chargeId)} className={linkCls}>Open →</button>
                          ) : (
                            <span title={s.chargeId}>{s.chargeId.slice(0, 8)}</span>
                          )
                        ) : s.paidAt ? "Zero share" : "—"}
                      </Td>
                      <Td className={mono}><PeriodLink id={s.periodId} /></Td>
                      <Td className={muted}>{formatDateTime(s.createdAt)}</Td>
                    </tr>
                  )}
                />
              </Panel>

              <Panel title="Charges">
                <QueueTable
                  columns={CHARGE_COLUMNS}
                  rows={c.charges}
                  renderRow={(ch) => {
                    const [label, t] = CONNECTION_CHARGE_STATE[ch.state] ?? [ch.state, "text-muted-foreground"];
                    return (
                      <tr key={ch.id} className="align-top">
                        <Td className={mono}>
                          {canCharge ? (
                            <button type="button" onClick={() => setCharge(ch.id)} className={linkCls}>{ch.ref}</button>
                          ) : (
                            ch.ref
                          )}
                        </Td>
                        <Td className={`text-xs font-medium ${t}`}>{label}<ReleaseRetry c={ch} /></Td>
                        <Td className={muted}>{formatDateTime(ch.createdAt)}</Td>
                        <Td align="right"><Money value={ch.profit} /></Td>
                        <Td align="right" className={mono}>{ch.cutPercent}%</Td>
                        <Td align="right"><Money value={ch.share} /></Td>
                        <Td className="whitespace-nowrap text-xs">{paidToLabel(ch.paidTo)}</Td>
                        <Td className={mono}>{orDash(ch.debitTicket)}</Td>
                        <Td className={mono}>{orDash(ch.creditTicket)}</Td>
                        <Td className={mono}>{orDash(ch.refundTicket)}</Td>
                        <Td className={muted}>{ch.releaseAt ? formatDateTime(ch.releaseAt) : orDash(null)}</Td>
                        <Td className={muted}>{ch.releasedAt ? formatDateTime(ch.releasedAt) : orDash(null)}</Td>
                        <Td className={mono}><PeriodLink id={ch.periodId} /></Td>
                        <Td className="min-w-[16rem]">
                          {ch.deals == null ? (
                            <span className="text-xs text-amber-400">Deals unavailable</span>
                          ) : ch.deals.length === 0 ? (
                            <span className="text-xs text-muted-foreground">No tickets</span>
                          ) : (
                            <div className="space-y-2">
                              {ch.deals.map((d) => (
                                <DealCard key={d.deal} label={dealLabel(ch, d)} deal={d} />
                              ))}
                            </div>
                          )}
                        </Td>
                      </tr>
                    );
                  }}
                />
              </Panel>
            </div>
          );
        }}
      </QueryState>
      {/* Who ended it (026). */}
      {can("audit.read") && (
        <Panel title="History">
          <RecordHistory type="connection" id={id} />
        </Panel>
      )}
      <ChargeDrawer id={charge} onClose={() => setCharge(null)} />
    </QueueLayout>
  );
}
